/**
 * A identificação da abertura (E126.F2, GUS-105; E165.F1.T03, GUS-135, HTML 6.8): o número do
 * processo, a pasta do banco e o CPF do cliente, nessa ordem, no topo da tela e antes dos arquivos.
 * Na abertura do cliente no fluxo eletrônico os três são obrigatórios (`*`); na interna, opcionais.
 * O CPF informado é cotejo com o CPF lido dos arquivos (a conferência é do servidor, E165.F1.T04).
 *
 * E144.F3 (GUS-135): o prazo processual e a urgência entram no mesmo card, recolhidos, depois dos
 * três campos (as observações têm card próprio, depois dos arquivos).
 *
 * O erro de formato aparece enquanto se digita; o campo obrigatório vazio só trava o envio, com o
 * aviso da área de arquivos (como no molde, que não acusa campo antes de a pessoa preenchê-lo).
 */
import type { ChangeEvent, ReactNode } from 'react';

import { CasoField } from '@/components/NovoCaso/NovoCasoFormField';
import { Input } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { ErrosDaIdentificacao, IdentificacaoMinima as Identificacao } from '@/lib/abertura';
import { formatCnj, formatCpf } from '@/lib/masks';
import type { ProcessoData } from '@/lib/processos';

import { CartaoDaAbertura } from './PecasDaAbertura';

const t = ptBR.abertura;

export interface IdentificacaoMinimaProps {
  valor: Identificacao;
  onChange: (valor: Identificacao) => void;
  erros: ErrosDaIdentificacao;
  /** E165.F1.T03: a abertura do cliente no fluxo eletrônico exige os três campos. */
  obrigatoria: boolean;
  disabled?: boolean;
  /** O que o card leva depois dos três campos (o prazo processual e a urgência). */
  children?: ReactNode;
}

function rotulo(nome: string, obrigatoria: boolean): string {
  return obrigatoria ? `${nome} *` : `${nome} ${t.identificacao.opcional}`;
}

export function IdentificacaoMinima({
  valor,
  onChange,
  erros,
  obrigatoria,
  disabled = false,
  children,
}: IdentificacaoMinimaProps): JSX.Element {
  const ti = t.identificacao;
  const campo =
    (nome: keyof Identificacao, formatar: (v: string) => string = (v) => v) =>
    (e: ChangeEvent<HTMLInputElement>): void =>
      onChange({ ...valor, [nome]: formatar(e.target.value) });
  return (
    <CartaoDaAbertura titulo={ti.titulo} testId="abertura-identificacao">
      <div className="grid grid-cols-1 gap-5 md:grid-cols-[1.25fr_1fr_1fr]">
        <CasoField
          id="abertura-processo"
          label={rotulo(ti.processo, obrigatoria)}
          {...(erros.processo === 'invalido' ? { error: ti.processoInvalido } : {})}
        >
          <Input
            id="abertura-processo"
            data-testid="abertura-processo"
            value={valor.processo}
            disabled={disabled}
            required={obrigatoria}
            inputMode="numeric"
            autoComplete="off"
            className="font-mono"
            onChange={campo('processo', formatCnj)}
          />
        </CasoField>
        <CasoField id="abertura-pasta" label={rotulo(ti.pasta, obrigatoria)}>
          <Input
            id="abertura-pasta"
            data-testid="abertura-pasta"
            value={valor.pasta}
            disabled={disabled}
            required={obrigatoria}
            maxLength={255}
            autoComplete="off"
            onChange={campo('pasta')}
          />
        </CasoField>
        <div>
          <CasoField id="abertura-cpf" label={rotulo(ti.cpf, obrigatoria)}>
            <Input
              id="abertura-cpf"
              data-testid="abertura-cpf"
              value={valor.cpf}
              disabled={disabled}
              required={obrigatoria}
              inputMode="numeric"
              maxLength={14}
              autoComplete="off"
              aria-describedby="abertura-cpf-erro"
              aria-invalid={erros.cpf === 'invalido'}
              className="font-mono"
              onChange={campo('cpf', formatCpf)}
            />
          </CasoField>
          <small
            id="abertura-cpf-erro"
            role="status"
            data-testid="abertura-cpf-erro"
            className="mt-1 block text-[13px] text-danger-700"
          >
            {erros.cpf === 'invalido' ? ti.cpfInvalido : ''}
          </small>
        </div>
      </div>
      {children}
    </CartaoDaAbertura>
  );
}

/** E126.F4: mais um exame no processo — a identificação é a dele, sem campo a preencher. */
export function ProcessoDoExame({
  processo,
  children,
}: {
  processo: ProcessoData;
  children?: ReactNode;
}): JSX.Element {
  const tp = t.processo;
  return (
    <CartaoDaAbertura titulo={tp.noProcesso} testId="abertura-no-processo">
      <p className="text-body-sm text-fg-muted">{tp.noProcessoTexto}</p>
      <dl className="mt-sc-3 grid grid-cols-[auto_1fr] gap-x-sc-4 gap-y-1 text-body-sm">
        <dt className="text-fg-muted">{tp.pasta}</dt>
        <dd className="font-semibold text-fg">{processo.pasta ?? tp.semPasta}</dd>
        <dt className="text-fg-muted">{tp.numero}</dt>
        <dd className="font-mono font-semibold text-fg">
          {processo.process_number ?? tp.numeroAusente}
        </dd>
      </dl>
      {children}
    </CartaoDaAbertura>
  );
}
